import {
    useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useSelector } from 'BMapsRedux';
import {
    setSidePanelMinimized,
    setSidePanelPaneWidth,
} from 'BMapsRedux/projectState/access';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { ensureArray } from 'BMapsSrc/util/js_utils';
import useEventBrokerSubscription from '../common/helpers/useEventBrokerSubscription';
import SidePanel from './SidePanel';
import SidePanelResizeHandle from './SidePanelResizeHandle';
import { SidePanelContext } from './sidePanelContext';
import {
    migratePersistedShape,
    clampPaneToBudget,
    DEFAULT_STACKABLE_PANE_IDS,
} from './sidePanelHelpers';

const DEFAULT_RAIL_ICONS = {
    help: 'fa fa-comments',
    workflow: 'fa fa-tasks',
    results: 'fa fa-list-ol',
    knowledge: 'fa fa-book',
    docking: 'fa fa-cubes',
};

/**
 * SidePanelLayout — wraps a `<SidePanel>` column with
 *   (a) horizontal drag-resize via per-pane SidePanelResizeHandles
 *   (b) PER-PANE width + minimize state in redux under
 *       sidePanels.<side>.panes.<pageId>, which is session-scoped; the durable
 *       copy is localStorage under `bmaps_sidepanel_panes_v1_<side>`
 *   (c) per-pane clamp against the viewport and against sibling panes. NOTE the
 *       cap fractions D6 specified (0.50 per pane, 0.60 combined) were both set
 *       to 1.0 on 2026-06-01, so there is no app-side maximum today — see the
 *       comment on PER_PANE_MAX_FRACTION in sidePanelHelpers
 *   (d) viewport gate: drag handle not rendered when window.innerWidth < 1024px
 *   (e) pane-aware context (D3) flows per-pane width + isResizing into
 *       descendant SidePanelContainers via useSidePanelPane(pageId)
 *
 * **SidePanel stays mounted in rail mode (Bug 4 fix, 2026-05-26).** Earlier
 * versions early-returned a rail-only render that excluded `<SidePanel>`.
 * That tore down `SidePanel.jsx`'s `setrightPanel` subscription, silently
 * dropping every page-open event published by external triggers
 * (RightControlPanel buttons, ChatBot actions, `UserActions.OpenSidePanel`).
 * Fix: SidePanel always mounts; in rail mode we hide it via width:0 +
 * visibility:hidden + overflow:hidden, so its event subscription survives.
 * A subscription on `setrightPanel` calls `expandFromRail()` whenever an
 * external page-open event arrives while minimized.
 *
 * **Side-by-side columns (2026-05-28).** When two or more stackable
 * pages are open, they render as adjacent columns inside the
 * `<SidePanel>` (which now uses flexDirection: 'row' in stacked mode).
 * Each pane owns its width via the per-pane slice. Rail mode is column-level:
 * the whole column collapses to the icon rail, on the COMBINED width of open
 * panes rather than any one pane's. ⚠️ Drag no longer triggers it — see
 * RAIL_COLLAPSE_ON_DRAG below — so the rail is reachable only from state that
 * already has it.
 */

// A stable identity for "this column has nothing persisted yet". Returning a
// fresh `{}` from the selector makes react-redux see a new reference on every
// call and re-render forever. No column is seeded in initial state - entries
// are created on first write - so this fallback is the normal case.
const NO_PERSISTED_STATE = {};
// Stable identity for "nothing reported yet", same reason as NO_PERSISTED_STATE.
const EMPTY_PAGE_IDS = [];
const MIN_VIEWPORT_PX = 1024;
const DEFAULT_REM_WIDTH = 22;
// Rail snaps on the COMBINED width of open panes, not per-pane: minimizing is
// one action on the whole column. The comparison is written inline at each of
// the drag sites below rather than shared. The fork extracted it as
// `computeRailSnapTrigger` in sidePanelHelpers.js (jfreeze95 fd0ee0943, PR #50)
// and then never called it; that helper was not migrated. See
// docs/gibbs-migration-register.md if consolidating.
const MINIMIZE_THRESHOLD_REM = 18;
// Drag-to-rail is off. Dragging a pane under the combined threshold collapsed the
// whole column to the icon rail, which overloaded "make this narrower" with a
// destructive mode change: against the 22rem default the trigger was ~4rem away, so
// the two gestures were nearly the same movement, and the way back was a different
// gesture in a different place. The rail's design is unsettled, so the behaviour is
// disabled rather than redesigned -- flip this to re-enable it unchanged.
//
// Only the two drag sites are gated. `expandFromRail` still reads
// MINIMIZE_THRESHOLD_REM for a DIFFERENT rule (per-pane, "is this persisted width
// still usable"), and still runs for a column already minimized.
const RAIL_COLLAPSE_ON_DRAG = false;
const RAIL_WIDTH_PX = 48;
// 2026-06-01: layout chrome constants for multi-pane drag math.
// SidePanelResizeHandle's `side='left'` formula computes
// `proposed = viewport - clientX - HANDLE_OFFSET`, which treats the
// dragged pane as if it sat at the rightmost edge of the viewport.
// When other panes (and their handles) sit between the dragged pane
// and viewport right, that formula overestimates the dragged pane's
// width by exactly that intervening width. Subtract it via
// `computePaneDragGeometry`, then also enforce an on-screen bound so
// the leftmost edge of `#rightsidepanel` can't slide past viewport
// left (which would push RightControlPanel + the drag handle off
// screen, exactly the "resize arrow goes off the screen" symptom).
const RIGHT_CONTROL_PANEL_PX = 80;
// 2026-06-01: handles narrowed from 12px to 8px in the visual redesign
// (three-dot grip replaces fa-arrows-h). Constants here must match the
// actual rendered width or the chrome-budget + position-offset math
// drifts (causing the ghost-line / committed-panel mismatch symptom).
const COL_LEVEL_HANDLE_PX = 8;
const PER_PANE_HANDLE_PX = 8;

/**
 * For a pane being dragged, compute (a) how much the
 * `vw - clientX - HANDLE_OFFSET` formula overestimates its width
 * because of panes-to-its-right, and (b) the maximum width that keeps
 * the whole `#rightsidepanel` group within the viewport AND prevents
 * the dragged pane from pushing sibling panes off-screen.
 *
 * 2026-06-01: extended to subtract panes-to-the-LEFT widths + their
 * handles from the chrome budget too. Previously only panes-to-right
 * were counted, which left an asymmetry: dragging Help (with Workflow
 * to its right) correctly stopped at the right boundary, but dragging
 * Workflow (with Help to its left) had no symmetric constraint and
 * could push Help off-screen leftward.
 */
function computePaneDragGeometry(
    target, paneStateMap, openStackableIds, vw, rightControlPanelPx,
) {
    const idx = openStackableIds.indexOf(target);
    if (idx < 0) return { offset: 0, effectiveMax: 0 };
    const panesToRight = openStackableIds.slice(idx + 1);
    const panesToLeft = openStackableIds.slice(0, idx);
    const widthsToRight = panesToRight.reduce(
        (acc, pid) => acc + (paneStateMap[pid]?.width || 0),
        0,
    );
    const widthsToLeft = panesToLeft.reduce(
        (acc, pid) => acc + (paneStateMap[pid]?.width || 0),
        0,
    );
    const handlesToRight = panesToRight.length * PER_PANE_HANDLE_PX;
    const handlesToLeft = panesToLeft.length * PER_PANE_HANDLE_PX;
    const offset = widthsToRight + handlesToRight;
    const rcpPx = (typeof rightControlPanelPx === 'number' && rightControlPanelPx > 0)
        ? rightControlPanelPx : RIGHT_CONTROL_PANEL_PX;
    const chromeWidth = rcpPx + COL_LEVEL_HANDLE_PX
        + widthsToRight + handlesToRight
        + widthsToLeft + handlesToLeft;
    const effectiveMax = Math.max(0, vw - chromeWidth);
    return { offset, effectiveMax };
}

function getRootFontPx() {
    if (typeof document === 'undefined') return 16;
    const v = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return Number.isFinite(v) && v > 0 ? v : 16;
}

/**
 * Should an incoming `set<side>Panel` event auto-expand the panel from rail?
 * Bug 4 fix policy — same as before, unchanged by this study.
 */
export function shouldExpandOnSetPanelEvent(eventData, isMinimized) {
    if (!isMinimized) return false;
    if (!eventData || typeof eventData !== 'object') return false;
    if (eventData.close === true) return false;
    if (eventData.zapAll === true) return false;
    if (!eventData.pageId) return false;
    return true;
}

// Nothing in here assumes a side: pass side="left" and it lays out a left
// column, persisting under `sidePanels.left` with its handles on the opposite
// edge. Only the right column mounts it today.
export default function SidePanelLayout({
    children, defaultPageId = 'help', side = 'right',
}) {
    // The column-level handle sits on the column's inboard edge.
    const columnHandleEdge = side === 'right' ? 'left' : 'right';
    // The event topic SidePanel builds from the same prop. A layout that stayed
    // on 'setrightPanel' would, on the left, subscribe to the OTHER column's
    // traffic and never hear its own.
    const panelTopic = `set${side}Panel`;
    // ── Determine which children declare themselves as stackable ─────
    // Stackable pane IDs come from children's `stackable` props on the
    // declared children of <SidePanelLayout>. This list seeds
    // legacy-width migration and informs the rail icon strip.
    const stackablePaneIds = useMemo(() => {
        const ids = ensureArray(children)
            .filter((child) => child && child.props
                && child.props.pageId && child.props.stackable)
            .map((child) => child.props.pageId);
        return ids.length > 0 ? ids : DEFAULT_STACKABLE_PANE_IDS;
    }, [children]);

    // ── Redux state ──────────────────────────────────────────────────
    const persisted = useSelector(
        (state) => state.projectState.sidePanels[side] || NO_PERSISTED_STATE,
    );

    // ── Viewport tracking ───────────────────────────────────────────
    const [viewportWidth, setViewportWidth] = useState(
        () => (typeof window !== 'undefined' ? window.innerWidth : 1920),
    );

    // ── RightControlPanel width (measured live) ─────────────────────
    // 2026-06-01: RIGHT_CONTROL_PANEL_PX was a 80px constant guess; the
    // actual width is `mol-button-group-width = 10vh + 1vw` from
    // proto/public/css/style.css and scales with viewport. Using the
    // wrong value caused the chrome budget to under-count, which
    // overflowed the viewport when panes were shrunk to fit (visible
    // as RightControlPanel button text clipping off the left edge).
    // Measure the actual rendered width by finding that control strip (the
    // first child of the column's own container) on mount + resize. The id is
    // derived from `side`: measuring `#rightsidepanel` from a left-mounted
    // column would budget for the OTHER column's chrome.
    const [rightControlPanelPx, setRightControlPanelPx] = useState(
        RIGHT_CONTROL_PANEL_PX,
    );
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        function measure() {
            const container = document.getElementById(`${side}sidepanel`);
            const first = container && container.firstElementChild;
            if (!first) return;
            const w = first.getBoundingClientRect().width;
            if (w > 0) setRightControlPanelPx(w);
        }
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [side]);

    // ── Per-pane state (D1, D3) ─────────────────────────────────────
    // useState init stays SSR-safe (no localStorage access — that
    // would create a hydration mismatch since server and client see
    // different sources). All localStorage work happens client-side
    // in the useEffect below. The Redux store has no persistence
    // middleware wired, so the predecessor study's criterion of
    // "widths survive a page reload" was never actually backed by
    // a persistent store; the localStorage layer below fills that gap.
    const PERSIST_KEY = `bmaps_sidepanel_panes_v1_${side}`;
    const [paneState, setPaneState] = useState(() => {
        const migrated = migratePersistedShape(persisted, stackablePaneIds);
        const root = getRootFontPx();
        const defaultPx = root * DEFAULT_REM_WIDTH;
        const initial = {};
        for (const pid of stackablePaneIds) {
            const mig = migrated && migrated[pid];
            const migWidth = (mig && Number.isFinite(mig.width) && mig.width > 0)
                ? mig.width : null;
            initial[pid] = {
                width: migWidth || defaultPx,
                isResizing: false,
                minimized: !!(mig && mig.minimized),
            };
        }
        return initial;
    });

    // Client-only localStorage sync: on first effect run after
    // hydration, READ saved widths and apply via setPaneState. On
    // every subsequent run (caused by a real user change), SAVE the
    // current paneState. The ref gate prevents the first run from
    // saving SSR defaults over the user's real persisted values.
    const hasHydratedRef = useRef(false);
    useEffect(() => {
        if (typeof localStorage === 'undefined') return;
        if (!hasHydratedRef.current) {
            hasHydratedRef.current = true;
            let parsed = null;
            try {
                const raw = localStorage.getItem(PERSIST_KEY);
                if (raw) parsed = JSON.parse(raw);
            } catch (e) { /* swallow */ }
            if (parsed && typeof parsed === 'object') {
                setPaneState((prev) => {
                    let changed = false;
                    const next = { ...prev };
                    for (const pid of Object.keys(parsed)) {
                        if (!next[pid]) continue;
                        const w = parsed[pid] && parsed[pid].width;
                        if (Number.isFinite(w) && w > 0 && w !== next[pid].width) {
                            next[pid] = {
                                ...next[pid],
                                width: w,
                                minimized: !!parsed[pid].minimized,
                            };
                            changed = true;
                        }
                    }
                    return changed ? next : prev;
                });
            }
            return;
        }
        try {
            const toSave = {};
            for (const pid of Object.keys(paneState)) {
                toSave[pid] = {
                    width: paneState[pid].width,
                    minimized: !!paneState[pid].minimized,
                };
            }
            localStorage.setItem(PERSIST_KEY, JSON.stringify(toSave));
        } catch (e) { /* swallow */ }
    }, [paneState, PERSIST_KEY]);

    // ── Column-level minimize (rail) state (D7) ─────────────────────
    const [minimized, setMinimized] = useState(() => !!persisted.minimized);

    // ── Which panes are open: reported by SidePanel, not re-derived ──
    // SidePanel decides this, in `computeVisiblePageIds` -- close, toggle,
    // zapAll + keepOpenOnZap, the stackable cap. The layout needs the same answer for
    // its drag math and takes it from there rather than deriving its own from the same
    // events. Don't reintroduce a second derivation: two copies of that rule drift, and
    // the symptom is a resize handle that disagrees with the column beside it.
    const [reportedOpenPageIds, setReportedOpenPageIds] = useState(EMPTY_PAGE_IDS);
    const reportOpenPages = useCallback((pageIds) => {
        // Compare by value: SidePanel reports on every commit of its page list, and an
        // unchanged set must not re-render the column (the ctx below is rebuilt from it).
        setReportedOpenPageIds((prev) => (
            prev.length === pageIds.length && prev.every((id, i) => id === pageIds[i])
                ? prev : pageIds));
    }, []);

    // Open stackable IDs preserves the declaration order from BMapsPage
    // (Help is declared first, so it's the leftmost when both are open).
    const openStackableIds = useMemo(
        () => stackablePaneIds.filter((pid) => reportedOpenPageIds.includes(pid)),
        [stackablePaneIds, reportedOpenPageIds],
    );

    // 2026-06-01: fit-to-viewport pass when the open set changes.
    // When a second pane opens while the first is at near-max width,
    // the combined widths overflow the viewport and the leftmost
    // chrome (RightControlPanel + col-handle) gets pushed off-screen
    // left. This effect proportionally shrinks the open panes to fit
    // whenever a NEW pane joins the open set. Removing a pane doesn't
    // shrink anything (the remaining pane keeps its width).
    const prevOpenStackableRef = useRef([]);
    useEffect(() => {
        const prev = prevOpenStackableRef.current;
        prevOpenStackableRef.current = openStackableIds;
        // Only run when a pane is ADDED (not removed)
        const added = openStackableIds.some((pid) => !prev.includes(pid));
        if (!added) return;
        if (openStackableIds.length === 0) return;
        const vw = typeof window !== 'undefined' ? window.innerWidth : viewportWidth;
        const handleCount = Math.max(0, openStackableIds.length - 1);
        const rcpPx = (rightControlPanelPx > 0
            ? rightControlPanelPx : RIGHT_CONTROL_PANEL_PX);
        const chrome = rcpPx + COL_LEVEL_HANDLE_PX
            + handleCount * PER_PANE_HANDLE_PX;
        const available = Math.max(0, vw - chrome);
        setPaneState((current) => {
            const totalCurrent = openStackableIds.reduce(
                (acc, pid) => acc + (current[pid]?.width || 0),
                0,
            );
            if (totalCurrent <= available) return current; // already fits
            const scale = available / totalCurrent;
            const next = { ...current };
            for (const pid of openStackableIds) {
                if (!current[pid]) continue;
                next[pid] = {
                    ...current[pid],
                    width: Math.floor((current[pid].width || 0) * scale),
                };
                // Persist so the shrink survives reload
                setSidePanelPaneWidth(pid, next[pid].width, side);
            }
            return next;
        });
    }, [openStackableIds, viewportWidth, rightControlPanelPx, side]);

    // ── Tell the app the geometry changed, after React has applied it ────
    // Not from the drag-end handler: `setPaneState` is async, so subscribers there
    // measure the PREVIOUS layout and the 3D viewer ends up one drag behind. Same
    // reason SidePanel publishes its own resize from an effect.
    //
    // Keyed on the committed widths, not `paneState`, so a re-render that moves no edge
    // does not republish.
    const committedPaneWidths = openStackableIds
        .map((pid) => paneState[pid]?.width)
        .join(',');
    useEffect(() => {
        EventBroker.publish('resize');
    }, [committedPaneWidths, minimized]);

    // ── Viewport resize → re-clamp each open pane ───────────────────
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        function onResize() {
            const vw = window.innerWidth;
            setViewportWidth(vw);
            setPaneState((current) => {
                const next = { ...current };
                for (const pid of Object.keys(next)) {
                    const others = Object.entries(current)
                        .filter(([otherId]) => otherId !== pid)
                        .map(([, p]) => p.width);
                    next[pid] = {
                        ...current[pid],
                        width: clampPaneToBudget(current[pid].width, others, vw),
                    };
                }
                return next;
            });
        }
        // 2026-06-01: sync viewportWidth from window.innerWidth on mount.
        // useState's lazy initializer above runs during server-side
        // render where window is undefined → state initialized to the
        // 1920 fallback. After client hydration, state stays at 1920
        // until a real window-resize event fires, which means the drag
        // math (computePaneDragGeometry uses viewportWidth state, not
        // window.innerWidth directly) computes against the wrong vw
        // until the user actively resizes the browser. This was the
        // 'doesn't match the ghost line until I resize the window'
        // symptom — once viewportWidth syncs, everything aligns.
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // ── Per-pane drag callbacks ─────────────────────────────────────
    // Each pane's <SidePanelResizeHandle> calls into these through the context.
    // The study routed them via a <PaneColumn> wrapper, which was not migrated and
    // which nothing rendered in the fork either, so SidePanel calls them directly.
    // The column-level handle resizes the leftmost open pane (D4).
    const handleColumnDragStart = useCallback(() => {
        setPaneState((current) => {
            const target = openStackableIds[0];
            if (!target) return current;
            return {
                ...current,
                [target]: { ...current[target], isResizing: true },
            };
        });
    }, [openStackableIds]);

    const handleColumnWidthChange = useCallback((proposed) => {
        const target = openStackableIds[0];
        if (!target) return;
        setPaneState((current) => {
            // 2026-06-01: read window.innerWidth at drag time rather than
            // the viewportWidth React state. State can lag the live
            // viewport if onResize hasn't fired yet, or if React hasn't
            // committed an update before the user drags — both produced
            // visible ghost-line / committed-edge mismatches on wider
            // screens. Reading the live value here eliminates the drift.
            const liveVw = (typeof window !== 'undefined'
                ? window.innerWidth : viewportWidth);
            const { offset, effectiveMax } = computePaneDragGeometry(
                target, current, openStackableIds, liveVw, rightControlPanelPx,
            );
            const corrected = proposed - offset;
            const clamped = Math.max(0, Math.min(corrected, effectiveMax));
            const root = getRootFontPx();
            const minimizeThresholdPx = root * MINIMIZE_THRESHOLD_REM;
            const combinedOpen = openStackableIds.reduce((acc, pid) => (
                acc + (pid === target ? clamped : (current[pid]?.width || 0))
            ), 0);
            if (RAIL_COLLAPSE_ON_DRAG && combinedOpen < minimizeThresholdPx) {
                setMinimized(true);
                return current;
            }
            setMinimized(false);
            return {
                ...current,
                [target]: { ...current[target], width: clamped },
            };
        });
    }, [openStackableIds, viewportWidth, rightControlPanelPx]);

    const handleColumnDragEnd = useCallback(() => {
        setPaneState((current) => {
            const target = openStackableIds[0];
            if (!target) return current;
            const next = {
                ...current,
                [target]: { ...current[target], isResizing: false },
            };
            // Persist per-pane width.
            setSidePanelPaneWidth(target, next[target].width, side);
            return next;
        });
        // Persist column-level minimize state.
        setSidePanelMinimized(minimized, side);
    }, [openStackableIds, minimized, side]);

    // ── Per-pane drag callbacks (2026-06-01) ────────────────────────
    // Parameterized by paneId — exposed via context for the per-pane
    // resize handles that live inside SidePanel's stacked render. Each
    // handle calls `ctx.beginPaneDrag(paneId)` on mousedown, then
    // `ctx.updatePaneWidth(paneId, proposed)` per mousemove, then
    // `ctx.endPaneDrag(paneId)` on mouseup. The clamp keeps the dragged
    // pane from overlapping its sibling (clampPaneToBudget subtracts
    // sibling widths from the total budget).
    const beginPaneDrag = useCallback((paneId) => {
        setPaneState((current) => {
            if (!current[paneId]) return current;
            return {
                ...current,
                [paneId]: { ...current[paneId], isResizing: true },
            };
        });
    }, []);

    const updatePaneWidth = useCallback((paneId, proposed) => {
        setPaneState((current) => {
            if (!current[paneId]) return current;
            // Same live-vw read as handleColumnWidthChange — see comment there.
            const liveVw = (typeof window !== 'undefined'
                ? window.innerWidth : viewportWidth);
            const { offset, effectiveMax } = computePaneDragGeometry(
                paneId, current, openStackableIds, liveVw, rightControlPanelPx,
            );
            const corrected = proposed - offset;
            const clamped = Math.max(0, Math.min(corrected, effectiveMax));
            const root = getRootFontPx();
            const minimizeThresholdPx = root * MINIMIZE_THRESHOLD_REM;
            const combinedOpen = openStackableIds.reduce((acc, pid) => (
                acc + (pid === paneId ? clamped : (current[pid]?.width || 0))
            ), 0);
            if (RAIL_COLLAPSE_ON_DRAG && combinedOpen < minimizeThresholdPx) {
                setMinimized(true);
                return current;
            }
            setMinimized(false);
            return {
                ...current,
                [paneId]: { ...current[paneId], width: clamped },
            };
        });
    }, [openStackableIds, viewportWidth, rightControlPanelPx]);

    const endPaneDrag = useCallback((paneId) => {
        setPaneState((current) => {
            if (!current[paneId]) return current;
            const next = {
                ...current,
                [paneId]: { ...current[paneId], isResizing: false },
            };
            setSidePanelPaneWidth(paneId, next[paneId].width, side);
            return next;
        });
        setSidePanelMinimized(minimized, side);
    }, [minimized, side]);

    // ── Expand-from-rail handler ────────────────────────────────────
    const expandFromRail = useCallback((pageIdToReopen) => {
        // Restore each pane to its persisted width (or canonical default
        // if the persisted value is below threshold). Per-pane widths
        // were preserved across the rail transition (D7) so this is
        // typically a no-op for the widths themselves.
        const root = getRootFontPx();
        const defaultPx = root * DEFAULT_REM_WIDTH;
        const minimizeThresholdPx = root * MINIMIZE_THRESHOLD_REM;
        setPaneState((current) => {
            const next = {};
            for (const pid of Object.keys(current)) {
                const w = current[pid].width >= minimizeThresholdPx
                    ? current[pid].width
                    : defaultPx;
                next[pid] = { ...current[pid], width: w };
                setSidePanelPaneWidth(pid, w, side);
            }
            return next;
        });
        setMinimized(false);
        setSidePanelMinimized(false, side);
        // 2026-06-01: when a rail-exit was triggered by a button click
        // with toggle:true (RightControlPanel's Workflow Checker button,
        // TopBar's HELP button), SidePanel.handleSetEvents will have
        // closed that pane as part of its toggle semantics — the page
        // was tracked as "open" while the column was minimized, and
        // toggle-on-open is a close. Re-fire a clean open event so the
        // page comes back into SidePanel's pageInfoList as visible. The
        // bug4RailReopen marker prevents handleExternalSetRightPanel
        // from re-expanding on this synthetic event.
        if (pageIdToReopen) {
            Promise.resolve().then(() => {
                EventBroker.publish(panelTopic, {
                    pageId: pageIdToReopen,
                    bug4RailReopen: true,
                });
            });
        }
    }, [side, panelTopic]);

    // ── Auto-expand when external publishers request a page open ────
    // Bug 4 fix preserved — SidePanel stays mounted in rail (handled
    // by the wrapper's CSS hide); this subscription handles the
    // expand-on-external-event side.
    const handleExternalSetRightPanel = useCallback((eventData) => {
        // The synthetic re-fire from expandFromRail tags itself so we
        // don't recurse here.
        if (eventData && eventData.bug4RailReopen === true) return;
        if (shouldExpandOnSetPanelEvent(eventData, minimized)) {
            expandFromRail(eventData.pageId);
        }
    }, [minimized, expandFromRail]);
    useEventBrokerSubscription(panelTopic, handleExternalSetRightPanel);

    // ── Disabled when viewport is too narrow ────────────────────────
    const handleDisabled = viewportWidth < MIN_VIEWPORT_PX;

    // ── No handle when there is nothing to resize ───────────────────
    // Both drag sites open `const target = openStackableIds[0]; if (!target) return;`,
    // so a handle here could only take the pointer and discard the result. Left out of
    // the tree rather than rendered disabled: the column is 0px wide in this state, so a
    // disabled handle shows as a floating stripe of panel chrome against the viewer.
    const nothingToResize = openStackableIds.length === 0;

    // ── Build the pane-aware context value ──────────────────────────
    // Legacy `width` / `isResizing` resolve to the first open pane's
    // values so existing consumers (CompoundTableSidePanel etc.) keep
    // working unchanged during the migration.
    const firstOpenPane = openStackableIds[0];
    const firstOpenPaneState = firstOpenPane ? paneState[firstOpenPane] : null;
    const openPaneWidth = firstOpenPaneState ? `${firstOpenPaneState.width}px` : null;

    const ctx = useMemo(() => ({
        // Tells a descendant SidePanel its dispatchers are real, not the
        // context defaults. Without it the per-pane handles never render.
        inLayout: true,
        reportOpenPages,
        // Legacy fields (D3 migration support)
        width: minimized ? `${RAIL_WIDTH_PX}px` : openPaneWidth,
        isResizing: !!(firstOpenPaneState && firstOpenPaneState.isResizing),
        minimized,
        expandFromRail,
        // Pane-aware fields (D3 new shape)
        panes: paneState,
        setPaneWidth: (pid, w) => {
            setPaneState((current) => ({
                ...current,
                [pid]: { ...current[pid], width: w },
            }));
            setSidePanelPaneWidth(pid, w, side);
        },
        // Per-pane drag dispatchers (2026-06-01) — consumed by the
        // per-pane SidePanelResizeHandles in SidePanel.jsx stacked render.
        beginPaneDrag,
        updatePaneWidth,
        endPaneDrag,
        handleDisabled,
    }), [paneState, minimized, expandFromRail, firstOpenPaneState, openPaneWidth, reportOpenPages,
        beginPaneDrag, updatePaneWidth, endPaneDrag, handleDisabled, side]);

    // Per-pane rail icons. Render one icon per stackable child.
    // Passes pageId through expandFromRail so the synthetic
    // bug4RailReopen path opens the requested pane in SidePanel.
    function expandAndFocus(pageId) {
        expandFromRail(pageId);
    }

    const railEntries = useMemo(() => ensureArray(children)
        .filter((child) => child && child.props && child.props.pageId
            && child.props.stackable)
        .map((child) => ({
            pageId: child.props.pageId,
            icon: child.props.railIcon
                || DEFAULT_RAIL_ICONS[child.props.pageId]
                || 'fa fa-folder',
            title: child.props.title || child.props.pageId,
        })), [children]);

    // ── Render ──────────────────────────────────────────────────────
    // Single render path regardless of minimized state. The SidePanel
    // is always mounted so its event subscription stays alive (Bug 4
    // fix preserved). The rail icons render in their own column
    // alongside the (hidden-when-minimized) SidePanel.
    return (
        <SidePanelContext.Provider value={ctx}>
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                height: '100%',
                alignItems: 'stretch',
            }}
            >
                {!handleDisabled && !nothingToResize && (
                    <SidePanelResizeHandle
                        side={columnHandleEdge}
                        onDragStart={handleColumnDragStart}
                        onWidthChange={handleColumnWidthChange}
                        onDragEnd={handleColumnDragEnd}
                    />
                )}

                {/* Rail icon strip — visible only when minimized */}
                {minimized && (
                    <div style={{
                        width: `${RAIL_WIDTH_PX}px`,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        paddingTop: '0.75rem',
                        gap: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.05)',
                    }}
                    >
                        {railEntries.length > 0 ? railEntries.map((e) => (
                            <button
                                key={e.pageId}
                                type="button"
                                onClick={() => expandAndFocus(e.pageId)}
                                title={`Expand ${e.title}`}
                                aria-label={`Expand ${e.title}`}
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    fontSize: '1.25rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <i className={e.icon} style={{ color: '#5d5d5d' }} />
                            </button>
                        )) : (
                            <button
                                type="button"
                                onClick={expandFromRail}
                                title="Click to expand the SidePanel"
                                aria-label="Expand the SidePanel"
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    fontSize: '1.25rem',
                                }}
                            >
                                <i
                                    className="fa fa-angle-double-left"
                                    style={{ color: '#5d5d5d' }}
                                />
                            </button>
                        )}
                    </div>
                )}

                {/*
                    SidePanel is ALWAYS rendered (Bug 4 fix). When in
                    rail mode, hide it via CSS — width:0 + visibility:
                    hidden + overflow:hidden — so the subtree stays
                    mounted and its `setrightPanel` subscription
                    survives. The SidePanel itself decides the
                    side-by-side row layout when isStacked (Phase 6).
                */}
                <div
                    data-testid="sidepanel-wrapper"
                    data-minimized={minimized ? 'true' : 'false'}
                    style={{
                        width: minimized ? '0px' : 'auto',
                        visibility: minimized ? 'hidden' : 'visible',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    aria-hidden={minimized ? 'true' : 'false'}
                >
                    <SidePanel side={side} defaultPageId={defaultPageId}>
                        {children}
                    </SidePanel>
                </div>
            </div>
        </SidePanelContext.Provider>
    );
}

// Legacy non-stackable side panels continue to call
// `setSidePanelWidth(value, tableKey)` directly from
// `BMapsRedux/projectState/access`. This module no longer dispatches it.
